import Vue from "vue";
import Vuex from "vuex";
import vuexI18n from "vuex-i18n";
import Api from "../api/orderkiosk";
import { checkSchedule, checkSchedules } from "@/utils/schedules";
import {
  annotateBasketVats,
  recalculateGroupPromotionPrices
} from "@/utils/pricing";
import { uuidv4 } from "@/utils/helpers";

import translationsEn from "../../public/i18n/en";
import translationsNl from "../../public/i18n/nl";
import translationsDe from "../../public/i18n/de";
import translationsEs from "../../public/i18n/es";
import translationsCa from "../../public/i18n/ca";

import { generateTemplateSettings } from "@/utils/templateSettings";
import _ from "lodash";

const DEFAULT_LOCALE = "nl";

// Dutch (nl)
// German (de)
// English (en)

Vue.use(Vuex);

function filterProducts(with_category, state) {
  let { products, currentCategory, schedules } = state;

  // return if category id is missing and we are looking for category products
  if (with_category && !currentCategory.id) return [];

  return products.reduce((acc, item) => {
    if (item.hide) return acc;

    if (!checkSchedules(schedules, item)) return acc;

    let soldout =
      item.soldout ||
      item.default_ingredients.some(
        ing => ing.default_count >= 1 && (ing.soldout || ing.hide)
      );

    let newItem = { ...item, soldout };

    let category_schedules_valid = true;

    if (with_category) {
      const cat_with_schedules = item.categories.find(
        i => i.category === currentCategory.id
      );
      if (cat_with_schedules) {
        category_schedules_valid = checkSchedules(
          schedules,
          cat_with_schedules
        );
      } else {
        category_schedules_valid = false;
      }
    }

    return category_schedules_valid &&
      (item.delivery_methods.length === 0 ||
        item.delivery_methods.includes(state.delivery_method))
      ? [...acc, newItem]
      : acc;
  }, []);
}

function getActiveMediaWithReplacements(state, id, replacementCount = 0) {
  if (!id) return;

  const media = state.css_theme_media[id];
  if (!media) return;

  if (
    replacementCount < 10 &&
    media.replacements &&
    media.replacements.length
  ) {
    for (const replacement of media.replacements) {
      if (checkSchedule(state.schedules, replacement.schedule)) {
        return getActiveMediaWithReplacements(
          state,
          replacement.replacement_media,
          replacementCount + 1
        );
      }
    }
  }

  return media;
}

const store = new Vuex.Store({
  state: {
    update_required: false,
    site_setting: {},
    template_setting: {},
    custom_home_setting: {},
    custom_home_active: false,
    delivery_method: null,
    extras: {},
    delivery_methods: [],
    categories: [],
    products: [],
    product_filters: [],
    offers: [],
    menus: [],
    promos: [],
    basket: [],
    coupons: [],
    screensavers: [],
    schedules: {},
    theme: {},
    css_theme_media: {},
    product_attributes: [],
    activeCombo: {},
    auditID: 0,
    currentCategory: {},
    project: process.env.VUE_APP_PROJECT,
    toTake: false,
    languages: [],
    available_languages: [],
    translations: {},
    currentLanguage: {},
    locked: false,
    lock_reason: {},
    customer_printer_response: "",
    next_puck: {},
    terminals: [],
    terminal: {},
    cikam_response: {},
    txid: null,
    puck_mode: null,
    manual_puck: null,
    itemsDict: {}
  },
  getters: {
    LANGUAGE_CODE: state => {
      if (!_.isEmpty(state.currentLanguage)) {
        return state.currentLanguage.code_short;
      }
      return state.site_setting.default_language.code_short;
    },
    LOCALE_CODE: state => {
      if (
        !_.isEmpty(state.currentLanguage) &&
        !_.isEmpty(state.currentLanguage.locale)
      ) {
        return state.currentLanguage.locale;
      } else if (
        !_.isEmpty(state.site_setting.default_language) &&
        !_.isEmpty(state.site_setting.default_language.locale)
      ) {
        return state.site_setting.default_language.locale;
      } else {
        console.log("locale missing, setting default locale.");
        return DEFAULT_LOCALE;
      }
    },
    CURRENCY_CODE: state => {
      if (
        !_.isEmpty(state.site_setting.default_currency) &&
        !_.isEmpty(state.site_setting.default_currency.code)
      ) {
        return state.site_setting.default_currency.code;
      }
      return "EUR";
    },
    DELIVERY_METHOD_FULL: state => {
      return state.delivery_methods.find(dm => dm.id === state.delivery_method);
    },
    EXTRAS: state => state.extras,
    DELIVERY_METHODS: state => state.delivery_methods,
    SELECTED_DELIVERY_METHOD: state => state.delivery_method,
    ACTIVE_COMBO: state => state.activeCombo,
    TRANSLATIONS: state => state.translations,
    ACTIVE_TRANSLATION: state => {
      return state.translations[state.currentLanguage.code_short];
    },
    BASKET: state => state.basket,
    CATEGORIES: state => state.categories,
    CATEGORIES_FILTERED: state => {
      let { categories, schedules } = state;
      let resultCategories = [];
      categories.forEach(raw_category => {
        let simple_category = {
          ...raw_category,
          categorySchedules: raw_category.schedules.map(s => s.schedule)
        };
        if (simple_category.hide) {
          return;
        }
        if (
          simple_category.delivery_methods.length !== 0 &&
          !simple_category.delivery_methods.includes(state.delivery_method)
        ) {
          return;
        }
        if (raw_category.schedules.length === 0) {
          resultCategories.push(simple_category);
          return;
        }
        let order_by = simple_category.order_by;
        let one_inactive = false;
        raw_category.schedules.forEach(schedule => {
          if (checkSchedule(schedules, schedule.schedule)) {
            if (schedule.active) {
              order_by += schedule.category_offset;
            } else {
              one_inactive = true;
            }
          }
        });
        if (!one_inactive) {
          simple_category.order_by = order_by;
          resultCategories.push(simple_category);
        }
      });
      resultCategories.sort((a, b) => a.order_by - b.order_by);
      return resultCategories;
    },
    CURRENT_CATEGORY: state => state.currentCategory,
    COUPONS: state => state.coupons,
    SCREENSAVERS: state => state.screensavers,
    MENUS: ({ menus, schedules, delivery_method }) => {
      return menus.reduce((acc, item) => {
        return checkSchedules(schedules, item) &&
          (item.delivery_methods.length === 0 ||
            item.delivery_methods.includes(delivery_method))
          ? [...acc, item]
          : acc;
      }, []);
    },
    OFFERS: state => {
      return state.offers.filter(
        o =>
          o.delivery_methods.length === 0 ||
          o.delivery_methods.includes(state.delivery_method)
      );
    },
    PROJECT: state => state.project,
    PRODUCTS_FILTERED_BY_CATEGORY: state => {
      return filterProducts(true, state);
    },
    PRODUCTS_FILTERED: state => {
      return filterProducts(false, state);
    },
    PRODUCTS: state => state.products,
    PRODUCT_ATTRIBUTES: state => state.product_attributes,
    PRODUCT_BY_UUID: state => uuid => state.products.find(p => p.id === uuid),
    PRODUCT_BY_BARCODE: state => barcode =>
      state.products.find(
        p =>
          p.barcode === barcode || p.variants.some(v => v.barcode === barcode)
      ),
    PRODUCT_FILTERS: state => state.product_filters,
    PROMOS: state => {
      return state.promos.filter(
        p =>
          p.delivery_methods.length === 0 ||
          p.delivery_methods.includes(state.delivery_method)
      );
    },
    TO_TAKE: state => state.toTake,
    CURRENT_LANGUAGE: state => state.currentLanguage,
    LANGUAGES: state => state.languages,
    AVAILABLE_LANGUAGES: state => state.available_languages,
    AUDIT_ID: state => state.auditID,
    SCHEDULES: state => state.schedules,
    THEME: state => state.theme,
    CSS_THEME_MEDIA: state => state.css_theme_media,
    ACTIVE_MEDIA_OBJECT: state => id => {
      return getActiveMediaWithReplacements(state, id);
    },
    SITE_SETTING: state => state.site_setting,
    TEMPLATE_SETTING: state => state.template_setting,
    CUSTOM_HOME_SETTING: state => state.custom_home_setting,
    CUSTOM_HOME_ACTIVE: state => state.custom_home_active,
    UPDATE_REQUIRED: state => state.update_required,
    LOCKED: state => state.locked,
    LOCK_REASON: state => state.lock_reason,
    NEXT_PUCK: state => state.next_puck,
    TERMINALS: state => state.terminals,
    TERMINAL: state => state.terminal,
    CIKAM_RESPONSE: state => state.cikam_response,
    TXID: state => state.txid,
    IS_CIKAM: state =>
      state.terminal && state.terminal.terminal_type == "CIKAM",
    MANUAL_PUCK: state => state.manual_puck,
    PUCK_MODE: state => state.puck_mode,
    IS_MANUAL_MANDATORY: state => state.puck_mode === "manual_mandatory",
    IS_NUMPAD_ACTIVE: state =>
      state.puck_mode === "manual" || state.puck_mode === "manual_mandatory",
    ITEMS_DICT: state => state.itemsDict
  },
  mutations: {
    CLEAR_BASKET: state => {
      state.basket = [];
    },
    CLEAR_COUPONS: state => {
      state.coupons = [];
      annotateBasketVats(
        state.basket,
        state.site_setting.default_tax,
        state.coupons
      );
      // TODO: check if recalculateGroupPromotionPrices can be removed!
      recalculateGroupPromotionPrices(state.basket);
    },
    SET_UPDATE_REQUIRED: (state, payload) => {
      state.update_required = payload;
    },
    SET_LANGUAGE: (state, lang) => {
      state.currentLanguage = lang;
      Vue.i18n.set(lang.code_short); //Uncomment when i18n/de.js file will be updated
    },
    SET_ACTIVE_COMBO: (state, payload) => {
      state.activeCombo = payload;
    },
    SET_BASKET: (state, payload) => {
      annotateBasketVats(
        payload,
        state.site_setting.default_tax,
        state.coupons
      );
      recalculateGroupPromotionPrices(payload);
      state.basket = payload;
    },
    SET_SCREENSAVERS: (state, payload) => {
      state.screensavers = payload;
    },
    SET_CATEGORIES: (state, payload) => {
      state.categories = payload;
    },
    SET_CURRENT_CATEGORY: (state, payload) => {
      state.currentCategory =
        state.categories.find(item => item.id === payload) || {};
    },
    SET_MENUS: (state, payload) => {
      state.menus = payload;
    },
    SET_OFFERS: (state, payload) => {
      state.offers = payload;
    },
    UPDATE_AUDIT_ID: state => {
      state.auditID = uuidv4();
    },
    SET_PRODUCTS: (state, payload) => {
      state.products = payload;
      for (const item of Object.values(payload)) {
        state.itemsDict[item.id] = item;
      }
    },
    SET_INGREDIENTS: (state, payload) => {
      state.itemsDict = { ...state.itemsDict, ...payload };
    },
    SET_PRODUCT_FILTERS: (state, payload) => {
      state.product_filters = payload;
    },
    SET_PROMOS: (state, payload) => {
      state.promos = payload;
    },
    SET_SCHEDULE: (state, payload) => {
      state.schedules = payload;
    },
    SET_THEME: (state, payload) => {
      state.theme = payload;
    },
    SET_CSS_THEME_MEDIA: (state, payload) => {
      state.css_theme_media = payload;
    },
    SET_PRODUCT_ATTRIBUTES: (state, payload) => {
      state.product_attributes = payload;
    },
    SET_LANGUAGES: (state, payload) => {
      state.languages = payload;
    },
    SET_SITE_SETTING: (state, payload) => {
      if (payload.length !== 1) return;
      state.site_setting = payload[0]; //site setting is a singleton so only save first elem.
      state.available_languages = payload[0].available_languages;
      state.template_setting = generateTemplateSettings();
    },
    SET_TEMPLATE_SETTING: (state, payload) => {
      state.template_setting = payload;
    },
    SET_CUSTOM_HOME_SETTING: (state, payload) => {
      if (payload.length !== 1) {
        return;
      }
      state.custom_home_setting = payload[0]; //custom home setting is a singleton so only save first elem.
    },
    SET_CUSTOM_HOME_ACTIVE: (state, payload) => {
      state.custom_home_active = payload;
    },
    SET_TO_TAKE: (state, payload) => {
      state.toTake = payload;
    },
    SET_EXTRAS: (state, payload) => {
      state.extras = payload;
      for (const [key, value] of Object.entries(payload)) {
        state.itemsDict[key] = value;
      }
    },
    SET_DELIVERY_METHODS: (state, payload) => {
      state.delivery_methods = payload;
    },
    SET_SELECTED_DELIVERY_METHOD: (state, payload) => {
      state.delivery_method = payload;
    },
    ADD_COUPON: (state, payload) => {
      state.coupons = [payload];
      annotateBasketVats(
        state.basket,
        state.site_setting.default_tax,
        state.coupons
      );
      recalculateGroupPromotionPrices(state.basket);
    },
    LOCK: (state, payload) => {
      if (payload) {
        state.lock_reason = payload;
      }
      state.locked = true;
    },
    UNLOCK: state => {
      state.lock_reason = {};
      state.locked = false;
    },
    SET_NEXT_PUCK: (state, payload) => {
      state.next_puck = payload;
    },
    SET_TRANSLATION: (state, { lang, translation }) => {
      state.translations[lang] = translation;
    },
    ADD_TERMINAL: (state, payload) => {
      let terminal = state.terminals.find(
        terminal => payload.terminal === terminal.terminal
      );
      if (!terminal) {
        state.terminals.push(payload);
      }
    },
    SET_TERMINAL: (state, payload) => {
      state.terminal = payload;
    },
    SET_CIKAM_RESPONSE: (state, payload) => {
      state.cikam_response = payload;
    },
    SET_TXID: (state, payload) => {
      state.txid = payload;
    },
    SET_MANUAL_PUCK: (state, payload) => {
      state.manual_puck = payload;
    },
    SET_PUCK_MODE: (state, payload) => {
      state.puck_mode = payload;
    },
    SET_CUSTOMER_PRINTER_RESPONSE: (state, response) => {
      state.customer_printer_response = response;
    }
  },
  actions: {
    GET_EXTRAS: async context => {
      let response = await Api.getExtras();
      const extras_map = {};
      response.data.forEach(extra => (extras_map[extra.id] = extra));
      context.commit("SET_EXTRAS", extras_map);
    },
    GET_DELIVERY_METHODS: async context => {
      let response = await Api.getDeliveryMethods();
      context.commit("SET_DELIVERY_METHODS", response.data);
    },
    GET_CATEGORIES: async context => {
      let response = await Api.getCategories();
      context.commit("SET_CATEGORIES", response.data);
    },
    GET_PRODUCTS: async context => {
      let response = await Api.getProducts();
      let products = response.data;
      products.sort((p1, p2) => {
        if (p1.order_by === p2.order_by) return p1.name < p2.name ? -1 : 1;
        return p1.order_by - p2.order_by;
      });
      context.commit("SET_PRODUCTS", products);
    },
    GET_PRODUCT_FILTERS: async context => {
      let response = await Api.getProductFilters();
      context.commit("SET_PRODUCT_FILTERS", response.data);
    },
    GET_PROMOS: async context => {
      let response = await Api.getPromos();
      context.commit("SET_PROMOS", response.data);
    },
    GET_SCREENSAVERS: async context => {
      let response = await Api.getScreenssavers();
      context.commit("SET_SCREENSAVERS", response.data);
    },
    GET_SCHEDULE: async context => {
      let response = await Api.getSchedule();
      const obj = {};
      response.data.forEach(item => {
        obj[item.id] = item;
      });
      context.commit("SET_SCHEDULE", obj);
    },
    GET_THEME: async context => {
      let response = await Api.getTheme();
      context.commit("SET_THEME", response.data);
    },
    GET_CSS_THEME_MEDIA: async context => {
      let response = await Api.getCssThemeMedia();
      const obj = {};
      response.data.forEach(item => {
        obj[item.id] = item;
      });
      context.commit("SET_CSS_THEME_MEDIA", obj);
    },
    GET_PRODUCT_ATTRIBUTES: async context => {
      let response = await Api.getProductAttributes();
      context.commit("SET_PRODUCT_ATTRIBUTES", response.data);
    },
    GET_MENUS: async context => {
      let response = await Api.getMenus();
      context.commit("SET_MENUS", response.data);
    },
    GET_OFFERS: async context => {
      let response = await Api.getOffers();
      context.commit("SET_OFFERS", response.data);
    },
    GET_LANGUAGES: async context => {
      let response = await Api.getLanguages();
      context.commit("SET_LANGUAGES", response.data);
    },
    GET_SITE_SETTING: async context => {
      let response = await Api.getSiteSetting();
      context.commit("SET_SITE_SETTING", response.data);
      await context.dispatch("GET_LANGUAGES");
      if (context.state.languages.length > 0) {
        for (const itm of context.state.languages) {
          if (itm.code_short in context.state.translations) continue; //only get on page reload.
          let lang_resp = await Api.getLanguage(itm.url);
          context.commit("SET_TRANSLATION", {
            lang: itm.code_short,
            translation: lang_resp.data
          });
        }
      }
    },
    GET_CUSTOM_HOME_SETTING: async context => {
      let response = await Api.getCustomHomeSetting();
      context.commit("SET_CUSTOM_HOME_SETTING", response.data);
    },
    LOG_AUDIT: async (context, data) => {
      if (data.category === "PAGE" && data.event === "OPEN") {
        context.commit("UPDATE_AUDIT_ID");
      }
      if (window.mmsKiosk === undefined) {
        console.log(
          "Audit ID - ",
          context.state.auditID,
          " Audit info - ",
          data
        );
      } else {
        console.log(
          "Audit ID - ",
          context.state.auditID,
          " Audit info - ",
          JSON.stringify(data)
        );
      }
    },
    LOG_ROUTER: async (context, data) => {
      context.dispatch("LOG_AUDIT", {
        category: "PAGE",
        event: "NAVIGATION",
        data: data.name
      });
      data.router.push({ name: data.name });
    },
    ADD_TERMINAL: async (context, data) => {
      console.log("ACTION ADD_TERMINAL: " + data.terminal);
      context.commit("ADD_TERMINAL", data);
    },
    SET_TERMINAL: async (context, data) => {
      context.commit("SET_TERMINAL", data);
    },
    SET_CIKAM_RESPONSE: async (context, data) => {
      context.commit("SET_CIKAM_RESPONSE", data);
    },
    SET_TXID: async (context, data) => {
      context.commit("SET_TXID", data);
    },
    SET_POLELIGHT: async (context, color) => {
      if (context.state.customer_printer_response === "PAPER_NEAR_END") return;
      Api.switchPoleLight(color);
    }
  }
});

Vue.use(vuexI18n.plugin, store);

Vue.i18n.add("en", translationsEn);
Vue.i18n.add("nl", translationsNl);
Vue.i18n.add("de", translationsDe);
Vue.i18n.add("es", translationsEs);
Vue.i18n.add("ca", translationsCa);

Vue.i18n.set(DEFAULT_LOCALE);

export default store;
